import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, Download } from "lucide-react";
import { toast } from "sonner";

export default function ProposalViewer() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [isApproving, setIsApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Using api.proposals directly since we created it
  const proposal = useQuery(api.proposals.getPublicProposal, shareToken ? { shareToken } : "skip");

  const approveProposal = useMutation(api.proposals.approvePublicProposal);

  if (proposal === undefined) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (proposal === null) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-slate-900 text-slate-200">
        <h1 className="mb-2 text-2xl font-bold">Proposal Not Found</h1>
        <p className="text-slate-400">This link may be invalid or has expired.</p>
      </div>
    );
  }

  const handleApprove = async () => {
    if (!shareToken) return;

    setIsApproving(true);
    try {
      await approveProposal({ shareToken });
      setApproved(true);
      toast.success("Proposal approved successfully!");
    } catch (error: any) {
      toast.error(error.message || "Failed to approve proposal");
    } finally {
      setIsApproving(false);
    }
  };

  const handleDownload = () => {
    if (!shareToken) return;
    setIsDownloading(true);

    // Create an invisible link to trigger the download natively
    // Convex hosts HTTP actions on the .site domain, not the .cloud WebSocket domain
    const convexUrl = import.meta.env.VITE_CONVEX_URL || "";
    const siteUrl = convexUrl.replace(".cloud", ".site");
    const downloadUrl = `${siteUrl}/api/proposals/download?token=${shareToken}`;

    // Simulate slight delay so the user sees the button feedback before browser takes over
    setTimeout(() => {
      window.open(downloadUrl, "_blank");
      setIsDownloading(false);
    }, 800);
  };

  const isAlreadyAccepted = proposal.status === "accepted" || approved;

  return (
    <div className="relative min-h-screen bg-slate-900 pb-24">
      {/* The HTML Canvas Content */}
      <style>{`
        .admin-only-actions { display: none !important; }
        button[data-action="save_proposal"], button[data-action="approve_proposal"] { display: none !important; }
      `}</style>
      <div className="w-full" dangerouslySetInnerHTML={{ __html: proposal.htmlContent || "" }} />

      {/* Sticky Action Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-800 bg-slate-900/90 p-4 backdrop-blur-md sm:p-6">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">
              {proposal.bidDetails?.title || "Proposal"}
            </h3>
            <p className="text-sm text-slate-400">
              {isAlreadyAccepted
                ? "This proposal has been approved."
                : "Please review the proposal and click approve when ready."}
            </p>
          </div>

          <div>
            {isAlreadyAccepted ? (
              <Button
                disabled
                className="bg-emerald-600 text-white opacity-100 hover:bg-emerald-700"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Approved
              </Button>
            ) : (
              <Button
                onClick={handleApprove}
                disabled={isApproving}
                className="bg-indigo-600 text-white hover:bg-indigo-700"
              >
                {isApproving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Approving...
                  </>
                ) : (
                  "Approve Proposal"
                )}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={isDownloading}
              className="ml-3 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white"
            >
              {isDownloading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
